import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Squares2X2Icon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  UsersIcon,
  BookOpenIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { SearchableUserSelect } from '@/components/ui/SearchableUserSelect'
import {
  getStreams,
  createStream,
  updateStream,
  deleteStream,
  getGrades,
  type Stream as ApiStream,
  type Grade as ApiGrade,
  type CreateStreamRequest,
  type UpdateStreamRequest
} from '@/api/academicApi'
import { getUsers } from '@/api/usersApi'
import { getRoles } from '@/api/rolesApi'
import toast from 'react-hot-toast'

// Form schema for creating new stream
const addStreamSchema = z.object({
  name: z.string().min(1, 'Stream name is required'),
  gradeId: z.string().min(1, 'Grade is required'),
  capacity: z.number().min(1, 'Capacity must be at least 1').max(50, 'Capacity cannot exceed 50'),
  description: z.string().optional(),
  classTeacher: z.string().optional(),
  isActive: z.boolean().default(true),
})

type AddStreamData = z.infer<typeof addStreamSchema>

// Use API types
type Stream = ApiStream
type Grade = ApiGrade

const StreamsPage = () => {
  const navigate = useNavigate()
  const [streams, setStreams] = useState<Stream[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [gradeFilter, setGradeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingStream, setEditingStream] = useState<Stream | null>(null)
  const [selectedStream, setSelectedStream] = useState<Stream | null>(null)
  
  // Available grades for stream creation
  const [availableGrades, setAvailableGrades] = useState<Grade[]>([])
  
  // Available teachers for class teacher selection
  const [availableTeachers, setAvailableTeachers] = useState<Array<{id: string, name: string}>>([])
  
  // Stats state
  const [stats, setStats] = useState({ 
    total: 0, 
    active: 0, 
    inactive: 0,
    totalStudents: 0,
    averageCapacity: 0
  })

  const addStreamForm = useForm<AddStreamData>({
    resolver: zodResolver(addStreamSchema),
    defaultValues: {
      name: '',
      gradeId: '',
      capacity: 30, // Default capacity
      description: '',
      classTeacher: '',
      isActive: true,
    }
  })

  // Edit form for updating streams
  const editStreamForm = useForm<AddStreamData>({
    resolver: zodResolver(addStreamSchema),
    defaultValues: {
      name: '',
      gradeId: '',
      capacity: 30,
      description: '',
      classTeacher: '',
      isActive: true,
    }
  })

  // Debounce the search query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 400)
    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  // Fetch streams when filters change
  useEffect(() => {
    fetchStreams()
  }, [debouncedSearchQuery, gradeFilter, statusFilter])

  // Fetch grades and teachers on mount
  useEffect(() => {
    fetchGrades()
    fetchTeachers()
  }, [])

  const fetchGrades = async () => {
    try {
      const response = await getGrades()
      setAvailableGrades(response.grades)
    } catch (error) {
      console.error('Failed to fetch grades:', error)
      toast.error('Failed to load grades')
      setAvailableGrades([]) // Ensure it's always an array
    }
  }

  const fetchTeachers = async () => {
    try {
      // First, let's get the Teacher role ID
      const rolesResponse = await getRoles()
      const teacherRole = rolesResponse.data.find((role: any) => 
        role.name.toLowerCase().includes('teacher') || role.name.toLowerCase().includes('teaching')
      )
      
      if (!teacherRole) {
        console.warn('No teacher role found')
        setAvailableTeachers([])
        return
      }

      // Get users with teacher role
      const usersResponse = await getUsers({ 
        roleId: teacherRole.id, 
        limit: 100 // Get more teachers
      })
      
      const teachers = usersResponse.users.map(user => ({
        id: user.id,
        name: user.fullName
      }))
      
      setAvailableTeachers(teachers)
    } catch (error) {
      console.error('Failed to fetch teachers:', error)
      toast.error('Failed to load teachers')
      setAvailableTeachers([])
    }
  }

  const fetchStreams = async () => {
    // Only show full loading state on initial load
    if (streams.length === 0) {
      setLoading(true)
    }
    try {
      // Use server-side filtering
      const apiStreams = await getStreams({
        search: debouncedSearchQuery.trim() || undefined,
        gradeId: gradeFilter !== 'all' ? gradeFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter as 'active' | 'inactive' : undefined
      })

      // Normalize stream data with default values
      const normalizedStreams = apiStreams.map(stream => ({
        ...stream,
        lessonsCount: stream.lessonCount || stream.lessonsCount || 0,
        currentStudents: stream.currentStudents || 0,
        capacity: stream.capacity || 30 // Default capacity
      }))

      setStreams(normalizedStreams)

      // Calculate stats from actual data
      const totalStudents = normalizedStreams.reduce((sum, s) => sum + (s.currentStudents || 0), 0)
      setStats({
        total: normalizedStreams.length,
        active: normalizedStreams.filter(s => s.isActive).length,
        inactive: normalizedStreams.filter(s => !s.isActive).length,
        totalStudents: totalStudents,
        averageCapacity: 0, // Not used anymore
      })

    } catch (error) {
      console.error('Failed to fetch streams:', error)
      toast.error('Failed to load streams')
    } finally {
      setLoading(false)
    }
  }

  const generateStreamName = (gradeId: string) => {
    const selectedGrade = availableGrades.find(g => g.id === gradeId)
    if (!selectedGrade) return ''
    
    // Auto-generate next stream letter (A, B, C, etc.)
    const existingStreams = streams.filter(s => s.gradeId === gradeId)
    const nextLetter = String.fromCharCode(65 + existingStreams.length) // A=65, B=66, etc.
    return `${selectedGrade.name}${nextLetter}`
  }

  const handleAddStream = async (streamData: AddStreamData) => {
    try {
      const createData: CreateStreamRequest = {
        name: streamData.name,
        gradeId: streamData.gradeId,
        classTeacher: streamData.classTeacher,
        description: streamData.description
      }
      
      await createStream(createData)
      toast.success(`Stream "${streamData.name}" created successfully!`)
      setShowAddModal(false)
      addStreamForm.reset()
      fetchStreams()
      
    } catch (error: any) {
      console.error('Failed to create stream:', error)
      const message = error?.response?.data?.message || 'Failed to create stream'
      toast.error(message)
    }
  }

  const handleViewStream = (stream: Stream) => {
    setSelectedStream(stream)
  }

  const handleEditStream = (stream: Stream) => {
    setEditingStream(stream)
    editStreamForm.reset({
      name: stream.name,
      gradeId: stream.gradeId || '',
      capacity: stream.capacity || 30,
      description: stream.description || '',
      classTeacher: stream.classTeacher || '',
      isActive: stream.isActive,
    })
    setShowEditModal(true)
  }

  const handleSaveEdit = async (data: AddStreamData) => {
    if (!editingStream) return

    try {
      const updateData: UpdateStreamRequest = {
        name: data.name,
        classTeacher: data.classTeacher,
        description: data.description,
        isActive: data.isActive,
      }

      await updateStream(editingStream.id, updateData)
      toast.success(`"${data.name}" updated successfully!`)
      setShowEditModal(false)
      setEditingStream(null)
      editStreamForm.reset()
      fetchStreams()
    } catch (error: any) {
      console.error('Failed to update stream:', error)
      const message = error?.response?.data?.message || 'Failed to update stream'
      toast.error(message)
    }
  }

  const handleDeleteStream = async (stream: Stream) => {
    if ((stream.lessonsCount || 0) > 0) {
      toast.error(`Cannot delete "${stream.name}". It has ${stream.lessonsCount} lessons assigned. Please remove lessons first.`)
      return
    }

    if (confirm(`Are you sure you want to delete "${stream.name}"?`)) {
      try {
        await deleteStream(stream.id)
        toast.success(`"${stream.name}" deleted successfully!`)
        fetchStreams()
      } catch (error: any) {
        console.error('Failed to delete stream:', error)
        const message = error?.response?.data?.message || 'Failed to delete stream'
        toast.error(message)
      }
    }
  }

  const handleToggleStatus = async (stream: Stream) => {
    const newStatus = !stream.isActive
    const statusText = newStatus ? 'activated' : 'deactivated'

    try {
      await updateStream(stream.id, {
        isActive: newStatus,
      })
      toast.success(`"${stream.name}" ${statusText} successfully!`)
      fetchStreams()
    } catch (error: any) {
      console.error('Failed to toggle stream status:', error)
      const message = error?.response?.data?.message || 'Failed to update stream status'
      toast.error(message)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="dashboard-stat-card animate-pulse">
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="dashboard-card animate-pulse">
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Squares2X2Icon className="h-8 w-8 text-purple-600 mr-3" />
            Streams Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage streams within grade levels (Grade 5A, 5B, 5C, etc.)
          </p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Stream
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Streams</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Squares2X2Icon className="h-8 w-8 text-purple-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Streams</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Students</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalStudents}</p>
            </div>
            <UsersIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg. Capacity</p>
              <p className="text-2xl font-bold text-gray-900">{stats.averageCapacity}</p>
            </div>
            <CircleStackIcon className="h-8 w-8 text-indigo-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
            </div>
            <XCircleIcon className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
        <form className="flex-1 relative max-w-md" onSubmit={(e) => e.preventDefault()}>
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search streams, teachers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
              }
            }}
            className="form-input pl-10"
          />
        </form>
        
        <div className="flex items-center space-x-3">
          <select
            value={gradeFilter}
            onChange={(e) => setGradeFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Grades</option>
            {Array.isArray(availableGrades) && availableGrades.map(grade => (
              <option key={grade.id} value={grade.id}>
                {grade.name}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Streams Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {streams.map((stream) => (
          <motion.div
            key={stream.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="dashboard-card hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => handleViewStream(stream)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{stream.name.slice(-1)}</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{stream.name}</h3>
                  <p className="text-sm text-gray-600">{stream.gradeName}</p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                stream.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {stream.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            {/* Stream Stats */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <UsersIcon className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">Students</span>
                </div>
                <p className="text-lg font-bold mt-1 text-blue-900">
                  {stream.currentStudents || 0}
                  {stream.capacity > 0 && <span className="text-sm font-normal text-gray-500"> / {stream.capacity} in grade</span>}
                </p>
              </div>
              <div className="bg-indigo-50 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <BookOpenIcon className="h-4 w-4 text-indigo-600" />
                  <span className="text-sm font-medium text-indigo-900">Lessons</span>
                </div>
                <p className="text-lg font-bold text-indigo-900 mt-1">{stream.lessonsCount || 0}</p>
              </div>
            </div>

            {/* Class Details */}
            <div className="space-y-2 mb-4">
              {stream.classTeacher && (
                <div className="flex items-center space-x-2 text-sm">
                  <AcademicCapIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-900">{stream.classTeacher}</span>
                </div>
              )}
            </div>

            {/* Grade Distribution Bar */}
            {stream.capacity > 0 && (
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Grade Distribution</span>
                  <span>{Math.round(((stream.currentStudents || 0) / stream.capacity) * 100)}% of grade</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all bg-blue-500"
                    style={{ width: `${Math.min(((stream.currentStudents || 0) / stream.capacity) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <span className="text-xs text-gray-500">
                By {stream.createdBy}
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleEditStream(stream)
                  }}
                  className="btn btn-ghost text-xs"
                  title="Edit Stream"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleToggleStatus(stream)
                  }}
                  className={`btn btn-ghost text-xs ${stream.isActive ? 'text-orange-600' : 'text-green-600'}`}
                  title={stream.isActive ? 'Deactivate' : 'Activate'}
                >
                  {stream.isActive ? (
                    <XCircleIcon className="h-4 w-4" />
                  ) : (
                    <CheckCircleIcon className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteStream(stream)
                  }}
                  className="btn btn-ghost text-xs text-red-600"
                  title="Delete Stream"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {streams.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="text-center py-12">
            <Squares2X2Icon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No streams found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery || gradeFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first stream'
              }
            </p>
            <button 
              onClick={() => setShowAddModal(true)}
              className="mt-4 btn btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create First Stream
            </button>
          </div>
        </motion.div>
      )}

      {/* Add Stream Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Add New Stream</h3>
                    <p className="text-sm text-gray-500">Create a new stream within a grade</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={addStreamForm.handleSubmit(handleAddStream)} className="p-6 space-y-4" autoComplete="off">
                {/* Hidden fields to prevent autofill */}
                <input type="text" style={{display:'none'}} autoComplete="off" />
                <input type="email" style={{display:'none'}} autoComplete="off" />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Parent Grade
                    </label>
                    <select
                      {...addStreamForm.register('gradeId')}
                      onChange={(e) => {
                        addStreamForm.setValue('gradeId', e.target.value)
                        addStreamForm.setValue('name', generateStreamName(e.target.value))
                      }}
                      className="form-input"
                      autoComplete="off"
                    >
                      <option value="">Select Grade</option>
                      {Array.isArray(availableGrades) && availableGrades.map(grade => (
                        <option key={grade.id} value={grade.id}>
                          {grade.name}
                        </option>
                      ))}
                    </select>
                    {addStreamForm.formState.errors.gradeId && (
                      <p className="mt-1 text-sm text-red-600">{addStreamForm.formState.errors.gradeId.message}</p>
                    )}
                  </div>

                  <Input
                    {...addStreamForm.register('name')}
                    label="Stream Name"
                    placeholder="e.g., Grade 5A"
                    autoComplete="off"
                    data-lpignore="true"
                    error={addStreamForm.formState.errors.name?.message}
                  />

                  

                  <SearchableUserSelect
                    label="Class Teacher (Optional)"
                    placeholder="Search for class teacher..."
                    value={addStreamForm.watch('classTeacher')}
                    onChange={(_teacherId, teacher) => {
                      addStreamForm.setValue('classTeacher', teacher ? teacher.fullName : '')
                    }}
                    roleFilter="teacher"
                    error={addStreamForm.formState.errors.classTeacher?.message}
                  />

                  <div className="flex items-center space-x-2 pt-6">
                    <input
                      type="checkbox"
                      {...addStreamForm.register('isActive')}
                      className="h-4 w-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Stream is active
                    </label>
                  </div>

                  <div className="md:col-span-2">
                    <Input
                      {...addStreamForm.register('description')}
                      label="Description (Optional)"
                      placeholder="Brief description of this stream's focus or specialization"
                      autoComplete="off"
                      data-lpignore="true"
                      error={addStreamForm.formState.errors.description?.message}
                    />
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-purple-900">Stream Hierarchy</h5>
                      <div className="text-sm text-purple-700 mt-1 space-y-1">
                        <p>• <strong>Grade</strong> → Contains multiple streams (5A, 5B, 5C)</p>
                        <p>• <strong>Stream</strong> → Contains students and receives lessons</p>
                        <p>• <strong>Lessons</strong> → Can be assigned to multiple streams</p>
                        <p>• <strong>Classes</strong> → Specific lesson instances in streams</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Squares2X2Icon className="h-4 w-4 mr-2" />
                    Create Stream
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stream Details Modal */}
      <AnimatePresence>
        {selectedStream && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedStream(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-xl">{selectedStream.name.slice(-1)}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{selectedStream.name}</h3>
                      <p className="text-sm text-gray-500">{selectedStream.gradeName}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedStream(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Stream Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Stream Details</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Parent Grade</span>
                        <span className="text-sm text-gray-900 font-medium">{selectedStream.gradeName}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Students</span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedStream.currentStudents || 0}
                          {selectedStream.capacity > 0 && <span className="text-gray-500"> / {selectedStream.capacity} in grade</span>}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Class Teacher</span>
                        <span className="text-sm text-gray-900 font-medium">{selectedStream.classTeacher || 'Not assigned'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          selectedStream.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {selectedStream.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Academic Statistics */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Academic Overview</h4>
                    <div className="space-y-4">
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <div className="flex items-center space-x-3">
                          <BookOpenIcon className="h-6 w-6 text-indigo-600" />
                          <div>
                            <p className="text-lg font-bold text-indigo-900">{selectedStream.lessonsCount || 0}</p>
                            <p className="text-sm text-indigo-700">Assigned lessons</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center space-x-3">
                          <UsersIcon className="h-6 w-6 text-blue-600" />
                          <div>
                            <p className="text-lg font-bold text-blue-900">{selectedStream.currentStudents || 0}</p>
                            <p className="text-sm text-blue-700">
                              Enrolled students
                              {selectedStream.capacity > 0 && ` (${selectedStream.capacity} total in grade)`}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedStream.description && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selectedStream.description}</p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                <div className="flex justify-between">
                  <Button
                    onClick={() => {
                      setSelectedStream(null)
                      navigate(`/schoolAdmin/students?streamId=${selectedStream.id}&streamName=${encodeURIComponent(selectedStream.name)}&gradeName=${encodeURIComponent(selectedStream.gradeName || '')}`)
                    }}
                    variant="secondary"
                  >
                    <UsersIcon className="h-4 w-4 mr-2" />
                    View Students
                  </Button>
                  <div className="flex space-x-3">
                    <Button
                      onClick={() => {
                        setSelectedStream(null)
                        toast.success(`Manage lessons for ${selectedStream.name}`)
                      }}
                      variant="secondary"
                    >
                      <BookOpenIcon className="h-4 w-4 mr-2" />
                      Manage Lessons
                    </Button>
                    <Button
                      onClick={() => handleEditStream(selectedStream)}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit Stream
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Stream Modal */}
      <AnimatePresence>
        {showEditModal && editingStream && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowEditModal(false)
              setEditingStream(null)
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Edit Stream</h3>
                    <p className="text-sm text-gray-500">Update stream information</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowEditModal(false)
                      setEditingStream(null)
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={editStreamForm.handleSubmit(handleSaveEdit)} className="p-6 space-y-4" autoComplete="off">
                <input type="text" style={{display:'none'}} autoComplete="off" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Parent Grade
                    </label>
                    <select
                      {...editStreamForm.register('gradeId')}
                      className="form-input"
                      disabled
                    >
                      {Array.isArray(availableGrades) && availableGrades.map(grade => (
                        <option key={grade.id} value={grade.id}>
                          {grade.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Grade cannot be changed</p>
                  </div>

                  <Input
                    {...editStreamForm.register('name')}
                    label="Stream Name"
                    placeholder="e.g., Grade 5A"
                    autoComplete="off"
                    data-lpignore="true"
                    error={editStreamForm.formState.errors.name?.message}
                  />

                  <SearchableUserSelect
                    label="Class Teacher (Optional)"
                    placeholder="Search for class teacher..."
                    value={editStreamForm.watch('classTeacher')}
                    onChange={(_teacherId, teacher) => {
                      editStreamForm.setValue('classTeacher', teacher ? teacher.fullName : '')
                    }}
                    roleFilter="teacher"
                    error={editStreamForm.formState.errors.classTeacher?.message}
                  />

                  <div className="flex items-center space-x-2 pt-6">
                    <input
                      type="checkbox"
                      {...editStreamForm.register('isActive')}
                      className="h-4 w-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Stream is active
                    </label>
                  </div>

                  <div className="md:col-span-2">
                    <Input
                      {...editStreamForm.register('description')}
                      label="Description (Optional)"
                      placeholder="Brief description of this stream's focus or specialization"
                      autoComplete="off"
                      data-lpignore="true"
                      error={editStreamForm.formState.errors.description?.message}
                    />
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-purple-900">Stream Information</h5>
                      <div className="text-sm text-purple-700 mt-1 space-y-1">
                        <p>• Students: {editingStream.currentStudents || 0}</p>
                        <p>• Lessons: {editingStream.lessonsCount || 0}</p>
                        <p>• Deactivating a stream will hide it from new enrollments</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false)
                      setEditingStream(null)
                    }}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default StreamsPage